/* eslint-disable max-len */
import { Form, Modal, Row, Table } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import { JinaOsobaPodpisEditBtn } from 'components/molecules/Dokumenty/JinaOsobaPodpisEditBtn';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';

import { IDokJinaOsoba, dokJinaOsobyGet } from 'services/dokumenty/prikazy';
import { dokumentOsobyPDFdownload, dokumentZmenaStavuApi, odeslatKpodpisu } from 'services/dokumenty/smlouva';
import { banksignPodpisSmsRedirect, dokumentOsobySmsPodpisPUT } from 'services/dokumenty/sms';

import { EStavPodpisu, EStavZadostiDok, ETypDokumentu } from 'enums/dokumentyEnums';
import { ENotificationType } from 'enums/notificationType';
import routePathEnum from 'enums/routePathEnum';

import getLanguageObject from 'utils/getLanguageObject';

import { IJinaOsobaData } from '../../NovyDokument';

interface IPodpisyJina {
    prevStep: () => void;
    zadostID: number;
    jinaOsobaData: IJinaOsobaData;
}

export interface IPodpisFileList extends IFileList {
    tempnazev: string;
}

const PodpisyJina: React.FC<IPodpisyJina> = ({ zadostID, prevStep, jinaOsobaData }) => {
    const { confirm } = Modal;
    const { navigateWorkaround } = useNavigateWorkaround();
    const { profily, language, NotificationComponent, aktivniFirma, dokumenty } = useContext(Context);
    const [form] = Form.useForm();
    const [osoby, setOsoby] = useState<{ osoby: IDokJinaOsoba[] }>();
    const [loading, setLoading] = useState<boolean>(false);
    const [refetch, setRefetch] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<IDokJinaOsoba>();
    const [signType, setSignType] = useState<string>();
    const [certPodpisFilelist, setCertPodpisFilelist] = useState<IPodpisFileList[]>([]);
    const [CertErr, setCertErr] = useState<string>();
    const [renderEditBtn, setAllowEdit] = JinaOsobaPodpisEditBtn(zadostID, setLoading, setRefetch);
    const downloadFileName = `${jinaOsobaData?.osobaData?.spolecnost?.idmb + ' - '}${dokumenty?.selectedZadost?.isin ? dokumenty?.selectedZadost?.isin + ' - ' : ''}${getLanguageObject(language)[(dokumenty?.selectedZadost?.typ === ETypDokumentu.PRIKAZY ? 'DOKUMENTY_PRIKAZ_' : 'TYP_SMLOUVY_') + dokumenty?.selectedZadost?.podtyp]}.pdf`;
    const hasPersonsSigned = !!osoby?.osoby.filter(
        e => e.stavpodpisu === EStavPodpisu.PODEPSANO || e.stavpodpisu === EStavPodpisu.NEOVERENO
    ).length;
    const personsYetToSign = !!osoby?.osoby.filter(
        e => e.stavpodpisu !== EStavPodpisu.PODEPSANO && e.stavpodpisu !== EStavPodpisu.NEOVERENO
    ).length;
    const loggedUser = (osoby?.osoby || []).filter(osoba => osoba.uzivateloid === Number(profily?.id));

    useEffect(() => {
        setLoading(true);
        dokJinaOsobyGet(zadostID)
            .then(resp => {
                setOsoby(resp);
                setAllowEdit(false);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetch]);

    const uploadPropsPotvrzeni = {
        id: 'potvrzeniDownloadBtn',
        showDownloadIcon: true,
        onChange: (data: UploadChangeParam) => {
            if (data.fileList) {
                data.fileList.length && setLoading(true);
                setCertErr(undefined);
                setCertPodpisFilelist(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                uid: e.uid,
                                name: e.name.normalize(),
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : '',
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error',
                                tempnazev: e.response && e.response?.tempnazev ? e.response.tempnazev : undefined
                            };
                        })
                );
            }
            if (data.file.response) {
                setLoading(false);
            }
        },
        onRemove: () => {
            setCertPodpisFilelist([]);
        },
        onDownload: () => {
            setLoading(true);
            dokumentOsobyPDFdownload(zadostID, openModal!.id, downloadFileName, true).finally(() => setLoading(false));
        },
        fileList: certPodpisFilelist,
        buttonDisabled: !!certPodpisFilelist.length
    };

    const osobyCols = [
        {
            title: <TranslateText>JMENO</TranslateText>,
            dataIndex: 'jmeno',
            key: 'jmeno',
            render: (jmeno: string, row: IDokJinaOsoba) => `${jmeno} ${row.prijmeni}`
        },
        {
            title: <TranslateText>EMAIL</TranslateText>,
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: <TranslateText>TELEFON</TranslateText>,
            dataIndex: 'telefon',
            key: 'telefon'
        },
        {
            title: <TranslateText>STAV</TranslateText>,
            dataIndex: 'stavpodpisu',
            key: 'stavpodpisu',
            render: (stavpodpisu: string, row: IDokJinaOsoba) =>
                stavpodpisu !== EStavPodpisu.NEPODEPSAL ? (
                    <>
                        <TranslateText>
                            {stavpodpisu === EStavPodpisu.ODESLANO && row.uzivateloid === Number(profily?.id)
                                ? 'DOKUMENTY_STAVPODPISU_ODESLANO_SELF'
                                : 'DOKUMENTY_STAVPODPISU_' + stavpodpisu}
                        </TranslateText>
                        {(stavpodpisu === EStavPodpisu.PODEPSANO ? !!row.podepsano : !!row.datdanokpodp) && (
                            <Paragraph
                                text={
                                    ' ' +
                                    dayjs(
                                        stavpodpisu === EStavPodpisu.PODEPSANO ? row.podepsano : row.datdanokpodp
                                    ).format('DD.MM.YYYY HH:mm')
                                }
                            />
                        )}
                    </>
                ) : (
                    ''
                )
        },
        /* {
           
            render: (_a: string, row: IDokJinaOsoba, index: number) => (
                <>
                    {row.uzivateloid !== Number(profily?.id) && row.stavpodpisu !== EStavPodpisu.NEPODEPSAL && (
                        <AnchorComponent
                            id={`OsobyPodepsatBtn${index}`}
                            key={`OsobyPodepsatBtn${index}`}
                            onClick={() => {
                                confirm({
                                    title: <TranslateText>DOKUMENTY_NOVY_ODESLAT_KPODPISU_CONFIRM</TranslateText>,
                                    cancelText: <TranslateText>NE</TranslateText>,
                                    okText: <TranslateText>ANO</TranslateText>,
                                    onOk() {
                                        setLoading(true);
                                        odeslatKpodpisu(zadostID, row.id)
                                            .then(() => {
                                        
                                                row.typpodpisu && setSignType(row.typpodpisu);
                                                setRefetch(pv => !pv);
                                           
                                            })
                                            .finally(() => setLoading(false));
                                    }
                                });
                            }}
                        >
                            <TranslateText>ODESLAT_K_PODPISU</TranslateText>
                        </AnchorComponent>
                    )}
                </>
            )
        
        },*/
        {
            render: (_text: string, row: IDokJinaOsoba, index: number) =>
                row.fileoid && (
                    <AnchorComponent
                        id={`OsobyZobrazitBtn${index}`}
                        key={`OsobyZobrazitBtn${index}`}
                        onClick={() => {
                            setLoading(true);
                            dokumentOsobyPDFdownload(zadostID, row.id, downloadFileName, true).finally(() =>
                                setLoading(false)
                            );
                        }}
                    >
                        <TranslateText>STAHNOUT</TranslateText>
                    </AnchorComponent>
                )
        }
    ];

    return (
        <LoadingWrapper loading={loading}>
            <FormWrapper outForm={form} onFinish={() => {}} name="Osoby">
                <Title level={5} text="PRIKAZY_PODPISY_TITLE" />
                <Table
                    rowKey={(_, index) => index || 0}
                    locale={{
                        emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                    }}
                    style={{
                        margin: '30px 0px'
                    }}
                    columns={osobyCols}
                    dataSource={osoby?.osoby}
                    pagination={false}
                    rowClassName={(_a, i) => {
                        const classes = [];
                        if (i % 2) classes.push('tableRowGray');
                        return classes.join(' ');
                    }}
                />
                <Row className="button-row">
                    <ButtonComponent buttontext="ZPET" onClick={() => prevStep()} id="OsobyZpetBtn" type="default" />
                    {!!loggedUser.length &&
                        /* loggedUser[0].stavpodpisu !== EStavPodpisu.NEPODEPSAL*/ (loggedUser[0].stavpodpisu ===
                            EStavPodpisu.ODESLANO ||
                            loggedUser[0].stavpodpisu === EStavPodpisu.CHYBNYPODPIS) && (
                            <ButtonComponent
                                buttontext={
                                    loggedUser[0].stavpodpisu === EStavPodpisu.ODESLANO ||
                                    loggedUser[0].stavpodpisu === EStavPodpisu.CHYBNYPODPIS
                                        ? 'PODEPSAT'
                                        : 'ZNOVU_PODEPSAT'
                                }
                                id="OsobyPodepsatBtn"
                                type="primary"
                                onClick={() => {
                                    if (
                                        loggedUser[0].stavpodpisu === EStavPodpisu.ODESLANO ||
                                        loggedUser[0].stavpodpisu === EStavPodpisu.CHYBNYPODPIS
                                    ) {
                                        setOpenModal(loggedUser[0]);
                                        setSignType(loggedUser[0].typpodpisu);
                                    } else {
                                        confirm({
                                            title: <TranslateText>DOKUMENTY_OSOBY_ZMENIT</TranslateText>,
                                            cancelText: <TranslateText>NE</TranslateText>,
                                            okText: <TranslateText>ANO</TranslateText>,
                                            onOk() {
                                                setLoading(true);
                                                odeslatKpodpisu(zadostID, loggedUser[0].id)
                                                    .then(() => {
                                                        setOpenModal(loggedUser[0]);
                                                        setSignType(loggedUser[0].typpodpisu);
                                                    })
                                                    .finally(() => setLoading(false));
                                            }
                                        });
                                    }
                                }}
                            />
                        )}

                    <ButtonComponent
                        buttontext="ODESLAT_CDCP"
                        id="OsobyPokracovatBtn"
                        disabled={!!personsYetToSign}
                        type="primary"
                        onClick={() => {
                            setLoading(true);
                            dokumentZmenaStavuApi(zadostID, EStavZadostiDok.AUTKONTROLA, true)
                                .then(() => {
                                    NotificationComponent({
                                        type: ENotificationType.SUCCESS,
                                        description: 'LEI_SPARAVA_ZADOST_SUCCESS_TITLE'
                                    });
                                    navigateWorkaround(
                                        `/firma/${aktivniFirma?.iducet}${routePathEnum.DOKUMENTY_PREHLED}`
                                    );
                                })
                                .finally(() => setLoading(false));
                        }}
                    />
                </Row>
            </FormWrapper>
            <Modal
                title={<TranslateText>VYBERTE_SPUSOB_PODEPISOVANI</TranslateText>}
                open={!!openModal}
                onCancel={() => {
                    setOpenModal(undefined);
                }}
                destroyOnClose={true}
                footer={[
                    <Row className="button-row-modal">
                        <ButtonComponent
                            buttontext="ZPET"
                            onClick={() => setOpenModal(undefined)}
                            id="closeModaltBtn"
                            type="default"
                            disabled={loading}
                        />
                        <ButtonComponent
                            buttontext={signType === 'BI' ? 'PODEPSAT_BISIGN' : 'PODEPSAT_CERT'}
                            id="OsobyPokracovatBtn"
                            type="primary"
                            disabled={(signType !== 'BI' && !certPodpisFilelist.length) || !!CertErr || loading}
                            extramessage={CertErr ? 'DOKUMENTY_SOUBOR_' + CertErr : ''}
                            onClick={() => {
                                if (signType === 'BI') {
                                    setLoading(true);
                                    banksignPodpisSmsRedirect(zadostID, openModal!.id, true)
                                        .then(resp => resp?.redirect && window.location.replace(resp.redirect))
                                        .finally(() => setLoading(false));
                                } else {
                                    setLoading(true);
                                    dokumentOsobySmsPodpisPUT(
                                        zadostID,
                                        openModal!.id,
                                        certPodpisFilelist[0].tempnazev,
                                        true
                                    )
                                        .then(resp => {
                                            if (resp.stav === 'OK') {
                                                setRefetch(pv => !pv);
                                                setOpenModal(undefined);
                                            } else {
                                                setCertErr(resp.stav);
                                            }
                                        })
                                        .finally(() => setLoading(false));
                                }
                            }}
                        />
                    </Row>
                ]}
            >
                <LoadingWrapper loading={loading}>
                    <SelectComponent
                        labelParagraph="ZPUSOB_PODEPSANI"
                        onChange={val => setSignType(val)}
                        defaultValue={openModal?.typpodpisu}
                        name="podepsani"
                        data={[
                            {
                                labelcz: 'CERTIFIKAT',
                                value: 'CERT'
                            },
                            {
                                labelcz: 'BISIGN',
                                value: 'BI'
                            }
                        ]}
                    />
                    {signType === 'CERT' && (
                        <>
                            <ButtonComponent
                                buttontext={'PODEPSAT_CERT_BTN'}
                                id="OsobyCertBtn"
                                type="primary"
                                style={{
                                    marginTop: 26
                                }}
                                onClick={() => {
                                    setLoading(true);
                                    dokumentOsobyPDFdownload(zadostID, openModal!.id, downloadFileName, true).finally(
                                        () => setLoading(false)
                                    );
                                }}
                            />
                            <div>
                                <Form.Item
                                    style={{
                                        marginBottom: 16
                                    }}
                                    id="potvrzeni"
                                    name="potvrzeni"
                                    label={<Paragraph text={'FORMULARE_PODEPSANY_SOUBOR'} strong={true} />}
                                >
                                    <div
                                        style={{
                                            marginBottom: 22
                                        }}
                                    >
                                        <UploadComponent {...uploadPropsPotvrzeni} />
                                    </div>
                                </Form.Item>
                            </div>
                        </>
                    )}
                </LoadingWrapper>
            </Modal>
            {hasPersonsSigned && renderEditBtn()}
        </LoadingWrapper>
    );
};

export default PodpisyJina;
// export default {};
